import axios from 'axios'
import { useAuth } from '@/composables/useAuth'
import { useEprintFunnel } from '@/composables/format-edit/useEprintFunnel'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import type {
  EprintActions,
  EprintPaymentData,
  EprintSession
} from '@/composables/format-edit/useEprintSession'

type StripePayload = {
  paymentHistoryId?: string
  paymentIntentId?: string
  chargeId?: string
  message?: string
  code?: string
}

export function useEprintPayment(
  session: EprintSession,
  actions: EprintActions
) {
  const { user } = useAuth()
  const { varuna, message, cookies } = useHostServices()
  const funnel = useEprintFunnel()

  function savePaymentLog(
    paymentType: string,
    paymentCost: number,
    paymentStatus: string,
    transactionId = ''
  ) {
    const formData = new FormData()
    formData.append('uuid', user.value?.uid || '')
    formData.append('print_type', session.printType)
    formData.append('format_id', session.formatid)
    formData.append('payment_type', paymentType)
    formData.append('payment_cost', String(paymentCost))
    formData.append('payment_status', paymentStatus)
    if (transactionId) {
      formData.append('detail', `取引番号: ${transactionId}`)
    }
    return axios.post(varuna.endPoints.logConveniencePayment, formData, {
      timeout: 30000
    })
  }

  async function savePaymentLogAndGetId(
    paymentType: string,
    paymentCost: number,
    paymentStatus: boolean | string,
    detailOrTransactionId = ''
  ) {
    const formData = new FormData()
    formData.append('uuid', user.value?.uid || '')
    formData.append('print_type', session.printType)
    formData.append('format_id', session.formatid)
    formData.append('payment_type', paymentType)
    formData.append('payment_cost', String(paymentCost))
    formData.append('payment_status', paymentStatus === true ? 'true' : 'false')
    if (detailOrTransactionId) {
      if (detailOrTransactionId.startsWith('{')) {
        formData.append('detail', detailOrTransactionId)
      } else {
        formData.append('detail', `取引番号: ${detailOrTransactionId}`)
      }
    }
    const response = await axios.post(
      varuna.endPoints.logConveniencePayment,
      formData,
      { timeout: 30000 }
    )
    if (
      response.data.result === 'success' &&
      response.data.payment_history_id
    ) {
      return response.data.payment_history_id as string
    }
    throw new Error(
      `決済ログ保存に失敗しました: ${response.data.message || '不明なエラー'}`
    )
  }

  function updatePaymentLogStatus(status: string, transactionId = '') {
    if (transactionId) {
      savePaymentLog('paypay', session.eprintPrice, status, transactionId)
    }
  }

  function handleCreditCardPaymentStart() {
    session.isCreditCardProcessing = true
    funnel.send('credit_card_payment_start', {
      payment_method: 'stripe',
      price: session.eprintPrice,
      print_type: session.printType,
      format_id: session.formatid,
      funnel_session_id: session.funnelSessionId
    })
  }

  function stripeLastPayment(
    paymentData: StripePayload | undefined,
    paymentHistoryId?: string
  ): EprintPaymentData {
    return {
      paymentType: 'stripe',
      paymentCost: session.eprintPrice,
      printType: session.printType,
      formatId: session.formatid,
      paymentHistoryId,
      stripePaymentIntentId: paymentData?.paymentIntentId,
      stripeChargeId: paymentData?.chargeId,
      timestamp: new Date().toISOString()
    }
  }

  function handlePaymentSuccess(paymentData: unknown) {
    const payload = (paymentData || {}) as StripePayload
    session.isCreditCardProcessing = false
    session.isPrePayment = true
    funnel.send('payment_success', {
      payment_method: 'stripe',
      price: session.eprintPrice,
      print_type: session.printType,
      format_id: session.formatid,
      transaction_id: payload.paymentHistoryId || null,
      funnel_session_id: session.funnelSessionId
    })
    funnel.sendPurchase({
      transaction_id:
        'eprint_stripe_' + (payload.paymentHistoryId || Date.now()),
      value: session.eprintPrice,
      print_type: session.printType,
      product_name: funnel.getPrintTypeLabel(session.printType)
    })

    if (payload.paymentHistoryId) {
      session.lastPaymentData = stripeLastPayment(
        payload,
        payload.paymentHistoryId
      )
    } else {
      const stripeDetail = JSON.stringify({
        payment_intent_id: payload.paymentIntentId || null,
        charge_id: payload.chargeId || null,
        timestamp: new Date().toISOString()
      })
      savePaymentLogAndGetId('stripe', session.eprintPrice, true, stripeDetail)
        .then((paymentHistoryId) => {
          session.lastPaymentData = stripeLastPayment(payload, paymentHistoryId)
        })
        .catch((error) => {
          console.error('決済ログ保存エラー:', error)
          session.lastPaymentData = stripeLastPayment(payload)
        })
    }
    actions.eprintAtokakin(paymentData)
  }

  function handlePaymentError(error: unknown) {
    const err = (error || {}) as StripePayload
    funnel.send('payment_error', {
      payment_method: 'stripe',
      price: session.eprintPrice,
      print_type: session.printType,
      format_id: session.formatid,
      error_message: err.message || '不明なエラー',
      error_code: err.code || 'unknown',
      funnel_session_id: session.funnelSessionId
    })
    message({
      message: err.message || '決済に失敗しました。',
      type: 'error'
    })
    session.isCreditCardProcessing = false
  }

  function handlePaypayPayment() {
    funnel.send('paypay_payment_start', {
      payment_method: 'paypay',
      price: session.eprintPrice,
      print_type: session.printType,
      format_id: session.formatid,
      funnel_session_id: session.funnelSessionId
    })
    actions.handleGetPaypayCodeEprint()
  }

  async function handleEprintFailureRefund(errorMessage: string) {
    if (!session.lastPaymentData) {
      message({
        message:
          'コンビニプリント番号の発行に失敗しました。サポートが必要な場合は、お問い合わせください。',
        type: 'error',
        duration: 0,
        showClose: true
      })
      return
    }
    session.isRefundProcessing = true
    const pay = session.lastPaymentData
    funnel.send('refund_initiated', {
      payment_method: pay.paymentType,
      refund_reason: errorMessage,
      price: pay.paymentCost,
      print_type: pay.printType,
      format_id: pay.formatId,
      payment_history_id: pay.paymentHistoryId || null,
      funnel_session_id: session.funnelSessionId
    })
    try {
      const formData = new FormData()
      formData.append('uuid', user.value?.uid || '')
      formData.append(
        'refund_reason',
        `コンビニプリント処理失敗: ${errorMessage}`
      )
      if (pay.paymentHistoryId) {
        formData.append('payment_history_id', pay.paymentHistoryId)
      } else {
        formData.append('format_id', pay.formatId || '')
        formData.append('print_type', pay.printType || '')
        formData.append('payment_cost', String(pay.paymentCost || ''))
      }
      const response = await axios.post(
        varuna.endPoints.eprintRefund,
        formData,
        {
          timeout: 60000
        }
      )
      if (response.data.result === 'success') {
        funnel.send('refund_complete', {
          payment_method: pay.paymentType,
          refund_amount: pay.paymentCost,
          print_type: pay.printType,
          format_id: pay.formatId,
          funnel_session_id: session.funnelSessionId
        })
        message({
          message:
            'コンビニプリント番号の発行に失敗したため、返金処理を完了しました。',
          type: 'warning',
          duration: 0,
          showClose: true
        })
        session.isPrePayment = false
        session.lastPaymentData = null
        session.eprintResultCode = '100'
        if (pay.paymentType === 'paypay') {
          cookies.remove('paypay_payment_history_id')
        }
      } else if (response.data.result === 'manual_required') {
        funnel.send('refund_manual_required', {
          payment_method: pay.paymentType,
          refund_amount: pay.paymentCost,
          print_type: pay.printType,
          format_id: pay.formatId,
          funnel_session_id: session.funnelSessionId
        })
        message({
          message:
            'コンビニプリント番号の発行に失敗しました。サポートが必要な場合は、お問い合わせください。',
          type: 'error',
          duration: 0,
          showClose: true
        })
      } else {
        funnel.send('refund_failed', {
          payment_method: pay.paymentType,
          error_message: response.data.message || '返金処理に失敗しました',
          refund_amount: pay.paymentCost,
          print_type: pay.printType,
          format_id: pay.formatId,
          funnel_session_id: session.funnelSessionId
        })
        message({
          message:
            'コンビニプリント番号の発行に失敗しました。サポートが必要な場合は、お問い合わせください。',
          type: 'error',
          duration: 0,
          showClose: true
        })
      }
    } catch (raw) {
      const error = raw as { message?: string; response?: { status?: number } }
      funnel.send('refund_error', {
        payment_method: session.lastPaymentData
          ? session.lastPaymentData.paymentType
          : 'unknown',
        error_message: error.message || '返金処理中にエラーが発生しました',
        error_code: error.response ? error.response.status : 'network_error',
        funnel_session_id: session.funnelSessionId
      })
      message({
        message:
          'コンビニプリント番号の発行に失敗しました。サポートが必要な場合は、お問い合わせください。',
        type: 'error',
        duration: 0,
        showClose: true
      })
    } finally {
      session.isRefundProcessing = false
    }
  }

  return {
    savePaymentLog,
    savePaymentLogAndGetId,
    updatePaymentLogStatus,
    handleCreditCardPaymentStart,
    handlePaymentSuccess,
    handlePaymentError,
    handlePaypayPayment,
    handleEprintFailureRefund
  }
}
